import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Slider from "@mui/material/Slider";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { PageHeader } from "@/components/PageHeader";
import { QuotaUpgradeDialog } from "@/components/QuotaUpgradeDialog";
import { useQuotaError } from "@/hooks/useQuotaError";
import { CAPABILITIES } from "@/lib/quotas";
import { answerWithRAG, type RAGAnswer } from "@/lib/answer-question.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/app/qa")({
  beforeLoad: () => {
    throw redirect({ to: "/app/jobs" });
  },
  component: () => null,
});

const TONE_MARKS = [
  { value: 0, label: "Corporate" },
  { value: 1, label: "Startup" },
  { value: 2, label: "Creative" },
];

const SAMPLE_QUESTIONS = [
  "Tell me about a time you led a cross-functional project from scratch.",
  "Describe a technical decision you made that had significant impact.",
  "How do you handle disagreement with a senior stakeholder?",
];

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

export function QAPanel({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const [question, setQuestion] = useState("");
  const [tone, setTone] = useState(1);
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<RAGAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const quotaDialog = useQuotaError(CAPABILITIES.qa_answer);

  const toneKey: "corporate" | "startup" | "creative" =
    tone === 0 ? "corporate" : tone === 1 ? "startup" : "creative";

  const handleGenerate = async () => {
    setLoading(true);
    setAnswer(null);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const result = await answerWithRAG({
        data: { question, tone: toneKey },
        headers,
      });
      setAnswer(result);
    } catch (e) {
      if (quotaDialog.handle(e)) return;
      setError(e instanceof Error ? e.message : "Failed to generate answer.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Application Q&A"
          subtitle="STAR-method answers grounded in your real history. Powered by retrieval over your indexed profile."
        />
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            <TextField
              label="Application or interview question"
              placeholder="e.g. Tell me about a time you shipped something with a tight deadline…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Typography variant="overline" color="text.secondary" sx={{ mr: 1 }}>
                Try
              </Typography>
              {SAMPLE_QUESTIONS.map((q) => (
                <Chip
                  key={q}
                  label={q.slice(0, 40) + "…"}
                  size="small"
                  variant="outlined"
                  onClick={() => setQuestion(q)}
                  icon={<LightbulbOutlinedIcon />}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tone
              </Typography>
              <Box sx={{ px: 1.5 }}>
                <Slider
                  value={tone}
                  onChange={(_, v) => setTone(v as number)}
                  step={1}
                  min={0}
                  max={2}
                  marks={TONE_MARKS}
                />
              </Box>
            </Box>

            <Box>
              <Button
                variant="contained"
                size="large"
                disabled={loading || question.trim().length < 10}
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
                }
                onClick={handleGenerate}
              >
                {loading ? "Generating…" : "Generate STAR answer"}
              </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      {answer && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Polished answer</Typography>
                <Tooltip title="Copy">
                  <IconButton onClick={() => copy(answer.fullAnswer)} size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                {answer.fullAnswer}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                STAR breakdown
              </Typography>
              <Stack spacing={2}>
                {(["situation", "task", "action", "result"] as const).map((k) => (
                  <Box key={k}>
                    <Typography variant="overline" color="primary">
                      {k}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {answer[k]}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sources used ({answer.sources.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Top retrieved chunks from your indexed history. Higher similarity = more relevant.
              </Typography>
              <Stack spacing={1.5} divider={<Divider />}>
                {answer.sources.map((s, i) => (
                  <Box key={i}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                      <Chip size="small" label={s.source_type} color="primary" variant="outlined" />
                      {s.source_ref && (
                        <Typography variant="caption" color="text.secondary">
                          {s.source_ref}
                        </Typography>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Chip
                        size="small"
                        label={`${(s.similarity * 100).toFixed(0)}%`}
                        color={s.similarity > 0.75 ? "success" : "default"}
                      />
                    </Stack>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "background.default" }}>
                      <Typography variant="body2" color="text.secondary">
                        {s.excerpt}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      <QuotaUpgradeDialog
        open={quotaDialog.open}
        onClose={quotaDialog.close}
        quota={quotaDialog.quota}
        message={quotaDialog.message}
      />
    </Box>
  );
}
